import {of, from, forkJoin} from "rxjs"
import {catchError, mergeMap} from "rxjs/operators"

const getClientCapabilities = () => {
  if (typeof PublicKeyCredential === "undefined" || typeof PublicKeyCredential.getClientCapabilities === "undefined") {
    return of(null)
  } else {
    return of(PublicKeyCredential.getClientCapabilities).pipe(
      mergeMap(fn => from(fn())),
      catchError(() => {
        return of(null)
      })
    )
  }
}

const getUserVerifyingPlatformAuthenticatorAvailable = () => {
  // Null when the API is absent or the probe throws: false must only ever mean
  // the browser itself said false
  if (typeof PublicKeyCredential === "undefined" || typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "undefined")  {
    return of(null)
  } else {
    return of(PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable).pipe(
      mergeMap(fn => from(fn())),
      catchError(() => {
        return of(null)
      })
    )
  }
}

/**
 * Retrieves WebAuthn device capabilities asynchronously.
 * The Web Authentication APIs (`PublicKeyCredential.getClientCapabilities()` and `PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()`)
 * are inherently asynchronous, as they often involve user interaction (e.g., biometric prompts) or hardware communication.
 * This function returns an RxJS Observable to align with the asynchronous nature of these underlying APIs and
 * to maintain consistency with the RxJS-based epic architecture, ensuring non-blocking operations and a responsive user interface.
 *
 * This function attempts to use PublicKeyCredential.getClientCapabilities() for comprehensive data.
 * If not supported or an error occurs, it falls back to PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
 * for broader compatibility.
 *
 * @returns An Observable that emits an object containing WebAuthn capabilities.
 */
export const getWebAuthnCapabilities = () => {
  return forkJoin({
    clientCapabilities: getClientCapabilities(),
    isUserVerifyingPlatformAuthenticatorAvailable: getUserVerifyingPlatformAuthenticatorAvailable(),
  })
}

/**
 * Low-cardinality projection for Elastic: the raw getClientCapabilities() dictionary
 * is ~24 booleans no dashboard will split on. Null means the browser could not say;
 * probeFailed separates a broken probe from not-probed-yet.
 */
export const toWebAuthnTelemetry = (capabilities) => {
  const clientCapabilities = capabilities?.clientCapabilities
  return {
    // The isUVPAA fallback covers browsers too old for getClientCapabilities
    platformAuthenticator: clientCapabilities?.userVerifyingPlatformAuthenticator ??
      capabilities?.isUserVerifyingPlatformAuthenticatorAvailable ?? null,
    passkeySupport: clientCapabilities?.passkeyPlatformAuthenticator ?? null,
    conditionalUi: clientCapabilities?.conditionalGet ?? null,
    hybridTransport: clientCapabilities?.hybridTransport ?? null,
    probeFailed: Boolean(capabilities?.error)
  }
}
